/**
 * Géométrie du monde.
 *
 * Ce module n'importe rien — un test lit le fichier et le vérifie. Les
 * positions lui sont **données**, en mètres ; il n'intègre rien et n'invente
 * aucune masse. Il traduit un monde en formes, et rien d'autre.
 *
 * ## Les tailles sont vraies, et le plancher est angulaire
 *
 * Distances **et** tailles sont fidèles : une planète a le rayon qu'elle a,
 * dans les mêmes unités que son orbite. C'est ce qui permet de descendre la
 * regarder de près au lieu de contempler une bille conventionnelle.
 *
 * Le prix, c'est qu'une planète vue depuis son orbite mesure quelques
 * millionièmes de l'écran : invisible, et surtout impossible à cliquer. Le
 * rayon dessiné est donc **le plus grand de deux** — le rayon vrai, et un
 * rayon qui garde au corps une taille apparente minimale. Ce second terme est
 * proportionnel à la distance à la caméra : c'est un **angle**, pas une
 * longueur, et c'est ce qui le rend inoffensif. En approchant, le rayon vrai
 * dépasse le plancher et prend le relais tout seul, sans bascule ni seuil.
 *
 * Un corps affiché à son plancher est donc un **repère de position**, pas une
 * mesure de taille. La vue le dit en toutes lettres.
 *
 * ## Le repère, et pourquoi il change
 *
 * Un monde qui va du mètre à l'unité astronomique ne tient pas dans les
 * nombres flottants d'une carte graphique, qui n'ont que sept chiffres : posé
 * à cent cinquante millions de kilomètres, un rocher de dix mètres n'a plus de
 * position distincte de sa voisine. Les positions sont donc exprimées **par
 * rapport à ce qu'on regarde** (`origin`) et divisées par une échelle
 * (`scale`) choisie pour ce qu'on regarde. Près de l'origine, la précision est
 * entière ; loin, elle se dégrade là où personne ne la mesure.
 *
 * L'invariant qui rend les changements de repère indolores est que **la
 * distance en mètres se conserve** : changer d'échelle change les nombres, pas
 * ce qu'on voit.
 *
 * ## Ce que la nature d'un corps commande, et ce qu'elle ne commande pas
 *
 * Elle commande la **matière** — une étoile émet, une planète reçoit — et rien
 * d'autre. Surtout pas la taille, qui est une donnée du corps.
 *
 * Hors du noyau : nombres nus et `Math` autorisés (les unités ici sont des
 * unités de scène, pas des grandeurs physiques).
 */

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export interface Point2 {
  readonly x: number;
  readonly y: number;
}

export type SceneKind = "star" | "planet" | "probe";

export interface Frame {
  /** Point du monde placé à l'origine de la scène, en mètres. */
  readonly origin: Point2;
  /** Mètres par unité de scène. */
  readonly scale: number;
}

export interface WorldSceneRequest {
  readonly frame: Frame;
  /** Position de la caméra, en unités de scène. Commande le plancher angulaire. */
  readonly camera: Vec3;
  /**
   * Rayon apparent minimal, en fraction de la distance à la caméra.
   *
   * C'est une tangente d'angle : la vue la calcule depuis sa hauteur en pixels
   * et son champ, parce qu'elle seule les connaît.
   */
  readonly minimumAngle: number;
  readonly selectedId: string;
  readonly entities: readonly SceneEntityRequest[];
}

export interface SceneEntityRequest {
  readonly id: string;
  readonly label: string;
  readonly kind: SceneKind;
  /** Rayon vrai, en mètres. */
  readonly radius: number;
  readonly position: Point2;
  /** Positions passées, de la plus ancienne à la plus récente, en mètres. */
  readonly trail: readonly Point2[];
}

export interface SceneEntitySketch {
  readonly id: string;
  readonly label: string;
  readonly kind: SceneKind;
  readonly centre: Vec3;
  /** Rayon dessiné, en unités de scène : le vrai, ou le plancher angulaire. */
  readonly radius: number;
  /** Vrai quand le plancher a pris le relais : ce n'est plus une taille. */
  readonly floored: boolean;
  readonly selected: boolean;
  readonly trail: readonly Vec3[];
}

export interface WorldScene {
  readonly entities: readonly SceneEntitySketch[];
  /** Rayon de la scène occupée, en unités de scène. Sert au cadrage. */
  readonly extent: number;
}

/** Le halo de sélection déborde du corps : la sélection se lit à la forme. */
const SELECTION_LIFT = 1.6;
/**
 * Recul de la caméra quand elle vise un corps, en multiples de son rayon.
 *
 * Assez près pour que le corps remplisse le regard, assez loin pour qu'on voie
 * ce qu'il y a autour — sans quoi « aller voir une planète » revient à se
 * coller dessus et à perdre le système de vue.
 */
const FOCUS_DISTANCE = 4.5;
/**
 * Ratio minimal retenu pour calculer un recul de caméra en portrait extrême.
 * En dessous, continuer à reculer rendrait le monde minuscule sans apporter
 * d'espace horizontal réellement utile.
 */
const MINIMUM_ASPECT = 0.25;
/** Un peu d'air latéral évite de coller les corps aux bords en portrait. */
const PORTRAIT_MARGIN = 1.25;
/**
 * Échelle minimale d'un repère, en mètres par unité de scène.
 *
 * Sans plancher, viser une sonde d'un mètre poserait le repère à un mètre par
 * unité, et l'étoile se retrouverait à 10¹¹ unités — au-delà de ce qu'un
 * flottant de carte graphique distingue de l'infini.
 */
const MINIMUM_SCALE = 1e3;

function vec(x: number, y: number, z: number): Vec3 {
  return { x, y, z };
}

/**
 * Le repère adapté à ce qu'on regarde.
 *
 * Sans corps visé, c'est le repère du système : origine au centre, une unité
 * astronomique par unité de scène. Avec un corps visé, l'origine se pose sur
 * lui et l'unité devient son rayon — il se dessine alors comme une sphère
 * unité, quelle que soit sa taille réelle, et tout ce qui l'entoure se lit en
 * multiples de lui-même.
 */
export function frameFor(
  focus: { position: Point2; radius: number } | undefined,
  systemScale: number,
): Frame {
  if (focus === undefined) {
    return { origin: { x: 0, y: 0 }, scale: Math.max(systemScale, MINIMUM_SCALE) };
  }
  return { origin: focus.position, scale: Math.max(focus.radius, MINIMUM_SCALE) };
}

/** Le plan du système est posé à plat : le `y` du calcul devient le `z` de la scène. */
function toScene(point: Point2, frame: Frame): Vec3 {
  return vec(
    (point.x - frame.origin.x) / frame.scale,
    0,
    (point.y - frame.origin.y) / frame.scale,
  );
}

/**
 * Rayon dessiné : le vrai, ou le plancher angulaire si le vrai est trop petit
 * pour qu'on voie encore le corps.
 *
 * Le plancher est proportionnel à la distance à la caméra, donc constant à
 * l'écran. En approchant, le rayon vrai finit par le dépasser et prend le
 * relais de lui-même : il n'y a ni seuil, ni bascule, ni discontinuité.
 */
export function apparentRadius(
  trueRadius: number,
  distanceToCamera: number,
  minimumAngle: number,
): number {
  const floor = Math.max(distanceToCamera, 0) * Math.max(minimumAngle, 0);
  return Math.max(trueRadius > 0 ? trueRadius : 0, floor);
}

function distance(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z);
}

export function buildWorldScene(request: WorldSceneRequest): WorldScene {
  const frame: Frame = {
    origin: request.frame.origin,
    scale: request.frame.scale > 0 ? request.frame.scale : 1,
  };

  const entities = request.entities.map((one) => {
    const centre = toScene(one.position, frame);
    const trueRadius = one.radius > 0 ? one.radius / frame.scale : 0;
    const radius = apparentRadius(
      trueRadius,
      distance(centre, request.camera),
      request.minimumAngle,
    );

    return {
      id: one.id,
      label: one.label,
      kind: one.kind,
      centre,
      radius,
      floored: radius > trueRadius,
      selected: one.id === request.selectedId,
      trail: one.trail.map((point) => toScene(point, frame)),
    };
  });

  let extent = 0;
  for (const one of entities) {
    extent = Math.max(extent, Math.hypot(one.centre.x, one.centre.z));
  }

  // Un monde vide, ou réduit à un point, donnerait une étendue nulle — et une
  // caméra posée à une distance nulle ne regarde rien.
  return { entities, extent: Math.max(extent, 1) };
}

/** Le halo d'un corps sélectionné : un anneau un peu plus large que lui. */
export function selectionRadius(entity: SceneEntitySketch): number {
  return entity.radius * SELECTION_LIFT;
}

/**
 * À quelle distance se poser pour regarder un corps de ce rayon.
 *
 * Le rayon est celui **dessiné**, plancher compris : viser un corps affiché à
 * son plancher angulaire et se placer à son rayon vrai reviendrait à traverser
 * la planète pour finir à l'intérieur.
 */
export function focusDistance(drawnRadius: number): number {
  return Math.max(drawnRadius, 0) * FOCUS_DISTANCE;
}

/**
 * Recul nécessaire pour garder le même contenu visible quand l'écran devient
 * plus étroit que haut. Le champ vertical de la caméra ne change pas ; son
 * champ horizontal, lui, se resserre avec le ratio de la vue.
 */
export function fitDistance(distance: number, aspect: number): number {
  const safeAspect = Math.min(Math.max(aspect, MINIMUM_ASPECT), 1);
  const margin = aspect < 1 ? PORTRAIT_MARGIN : 1;
  return (Math.max(distance, 0) * margin) / safeAspect;
}

// ── Les sillages ─────────────────────────────────────────────────────────────

/**
 * Recopie un sillage dans un tampon alloué une fois pour toutes, et rend le
 * nombre de points effectivement écrits.
 *
 * ## Pourquoi cette fonction existe
 *
 * Elle remplace un appel à `BufferGeometry.setFromPoints` à chaque image, qui
 * **ne fonctionnait pas** : depuis plusieurs versions, cette méthode n'écrit
 * que `min(points.length, attribut.count)` valeurs quand l'attribut existe
 * déjà. Le sillage étant vide au moment de la construction, le compteur valait
 * zéro et n'en bougeait plus jamais. Aucune erreur, aucun avertissement — juste
 * des sillages invisibles, que seule une capture d'écran a révélés.
 *
 * Le tampon est donc alloué à sa taille maximale, rempli, et la portion à
 * dessiner est déclarée. C'est aussi ce qu'il fallait faire pour la
 * performance : plus une seule allocation par image.
 */
export function fillTrail(
  target: Float32Array,
  points: readonly Vec3[],
  lift: number,
): number {
  const count = Math.min(points.length, Math.floor(target.length / 3));
  // On garde la **fin** du sillage : les positions récentes sont celles qui
  // rejoignent le corps, et couper par le début laisserait une ligne flottante
  // détachée de la bille.
  const start = points.length - count;

  for (let index = 0; index < count; index += 1) {
    const point = points[start + index];
    if (point === undefined) continue;
    target[index * 3] = point.x;
    target[index * 3 + 1] = point.y + lift;
    target[index * 3 + 2] = point.z;
  }

  return count;
}

/**
 * Un pas d'approche vers une cible.
 *
 * La caméra ne saute pas d'un corps à l'autre : elle y va. Le déplacement est
 * une **fraction du chemin restant** par image, ce qui donne une approche qui
 * ralentit en arrivant, sans jamais dépasser ni osciller. Le seuil coupe la
 * queue asymptotique : sans lui, on n'arrive jamais tout à fait, et la caméra
 * garde la main sur la cible indéfiniment.
 */
export function approach(from: Vec3, to: Vec3, fraction: number): Vec3 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const dz = to.z - from.z;
  if (Math.hypot(dx, dy, dz) < 1e-4) return to;
  const step = Math.min(Math.max(fraction, 0), 1);
  return vec(from.x + dx * step, from.y + dy * step, from.z + dz * step);
}

// ── Les étiquettes ───────────────────────────────────────────────────────────

export interface LabelCandidate {
  readonly id: string;
  /** Position à l'écran, en pixels. */
  readonly x: number;
  readonly y: number;
  /** Qui garde son étiquette en cas de collision : le plus grand nombre gagne. */
  readonly priority: number;
}

/**
 * Choisit les étiquettes à afficher quand plusieurs corps se chevauchent.
 *
 * Quand une étoile de passage traverse un système, les corps se retrouvent à
 * quelques pixels les uns des autres et leurs noms se superposent jusqu'à
 * devenir un pâté illisible — c'est ce qu'une capture du scénario « Rencontre »
 * a montré. On garde alors la plus prioritaire (le corps sélectionné, puis le
 * plus gros) et on tait les autres, plutôt que d'empiler du texte.
 *
 * Le tri est **total et déterministe** : à priorité égale, l'identifiant
 * tranche. Sans ça, deux corps de même masse verraient leur étiquette clignoter
 * d'une image à l'autre au gré de l'ordre de tri.
 */
export function visibleLabels(
  candidates: readonly LabelCandidate[],
  gapX: number,
  gapY: number,
): ReadonlySet<string> {
  const ordered = [...candidates].sort(
    (a, b) => b.priority - a.priority || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );

  const kept: LabelCandidate[] = [];
  const shown = new Set<string>();

  for (const candidate of ordered) {
    const collides = kept.some(
      (placed) =>
        Math.abs(placed.x - candidate.x) < gapX && Math.abs(placed.y - candidate.y) < gapY,
    );
    if (collides) continue;
    kept.push(candidate);
    shown.add(candidate.id);
  }

  return shown;
}
